"use client";

import { BreadcrumbItem, BreadcrumbProps } from "./types";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import React, { Fragment } from "react";

import { ShellLink } from "@/src/components";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { cn } from "utils";

export const DocsBreadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  separatorIcon = "chevron-right",
  className,
  maxItems = 3,
  showHomeIcon = true,
  homeHref = "/",
}) => {
  // 处理超出最大显示数量的情况
  const processedItems = React.useMemo(() => {
    if (items.length <= maxItems) {
      return items;
    }

    // 如果超出最大数量，保留第一个、最后一个，中间显示省略号
    const firstItem = items[0];
    const lastItems = items.slice(-2); // 保留最后两个

    return [
      firstItem,
      { label: "...", disabled: true } as BreadcrumbItem,
      ...lastItems,
    ];
  }, [items, maxItems]);

  const renderBreadcrumbItem = (
    item: BreadcrumbItem,
    _index: number,
    isLast: boolean,
  ) => {
    const itemContent = (
      <span
        className={cn("body_medium_s", {
          "text-content_tertiary max-w-[160px] line-clamp-1 overflow-hidden text-ellipsis ":
            isLast,
          "text-content_primary": !isLast && !item.disabled,
          "text-content_tertiary cursor-default": item.disabled,
          "cursor-pointer": !item.disabled && !isLast && item.href,
        })}
      >
        {item.label}
      </span>
    );

    // 如果是当前页面（最后一项）或禁用状态，不显示为链接
    if (isLast || item.disabled || !item.href) {
      return itemContent;
    }

    return (
      <ShellLink
        aria-label="Docs Breadcrumb"
        data-openreplay-label={`Docs Breadcrumb Link | ${item.href}`}
        id="lnk-docs-breadcrumb"
        href={item.href}
        style={{ display: "contents" }}
      >
        {itemContent}
      </ShellLink>
    );
  };

  return (
    <nav
      data-openreplay-label="Docs Breadcrumb nav Semantic"
      id="sem-docs-breadcrumb-nav"
      className={cn("flex items-center gap-2", className)}
      aria-label="breadcrumb"
    >
      {/* 主页图标 */}
      {showHomeIcon && (
        <>
          <ShellLink
            data-openreplay-label={`go home Link | ${homeHref}`}
            id="lnk-go-home"
            href={homeHref}
            className="inline-flex items-center text-content_secondary hover:text-content_primary transition-colors duration-200"
            aria-label="go home"
          >
            <Icon
              name="home"
              size={16}
              color={ospThemes.colors.content_secondary}
              className="hover:opacity-80"
            />
          </ShellLink>
          {processedItems.length > 0 && (
            <Icon
              name={separatorIcon}
              size={12}
              color={ospThemes.colors.content_tertiary}
            />
          )}
        </>
      )}

      {/* 面包屑项目 */}
      {processedItems.map((item, index) => {
        const isLast = index === processedItems.length - 1;

        return (
          <Fragment key={`${item.label}-${index}`}>
            {renderBreadcrumbItem(item, index, isLast)}

            {/* 分隔符 */}
            {!isLast && (
              <OspIcons
                name="chevron-right"
                size={12}
                color={ospThemes.colors.content_tertiary}
              />
            )}
          </Fragment>
        );
      })}
    </nav>
  );
};
